/**
 * The workbench's Checks panel: test runner output for a building.
 *
 * A run is Bear's built-in checks plus the checks each of the project's
 * `spec/tests/` files registers, all on the model on screen, executed by
 * `bear/test` in the sandbox. It runs whenever the model builds, whenever a
 * test file is added, removed or edited, and on demand. Results are
 * point-in-time: nothing is kept between runs.
 */

import type { CheckLayer, TestReport, TestResult } from "bear/test";
import { CircleCheck, CircleX, Loader2, Play } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { ChangeRow } from "@/components/TaskChanges";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { ItemGroup } from "@/components/ui/item";
import {
  runChecksInSandbox,
  subscribeExecution,
} from "@/lib/bear-sandbox-bridge";

/** Project check files: the test files under `spec/tests/`. */
export function isCheckFile(path: string): boolean {
  return /^spec\/tests\/.+\.test\.tsx?$/.test(path);
}

const LAYERS: Record<CheckLayer, string> = {
  bear: "Bear built-in",
  location: "Location",
  code: "Code",
  firm: "Firm",
  model: "Model-specific",
};
const LAYER_ORDER = Object.keys(LAYERS) as CheckLayer[];
const STATUS_ORDER: TestResult["status"][] = ["failed", "passed", "skipped"];

/** A test written without a layer lives in the project, so it is the model's own. */
function layerOf(result: TestResult): CheckLayer {
  return result.layer ?? "model";
}

/** Failures first, then by layer from Bear's own out to the model's. */
export function sortChecks(results: readonly TestResult[]): TestResult[] {
  return [...results].sort(
    (a, b) =>
      STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
      LAYER_ORDER.indexOf(layerOf(a)) - LAYER_ORDER.indexOf(layerOf(b)),
  );
}

export type ChecksState =
  | { status: "waiting" }
  | { status: "running"; last?: TestReport }
  | { status: "done"; report: TestReport }
  | { status: "error"; error: string };

/** A check file and when its body last changed (null for an unstamped inode). */
export interface CheckFile {
  path: string;
  mtime: number | null;
}

/**
 * Run the checks: after every successful build, when a check file is added,
 * removed or edited, and on `run()`. One run at a time; a request during a
 * run queues exactly one more.
 */
export function useTaskChecks(files: readonly CheckFile[]) {
  const [state, setState] = useState<ChecksState>({ status: "waiting" });
  const filesRef = useRef(files);
  filesRef.current = files;
  const running = useRef(false);
  const queued = useRef(false);
  const built = useRef(false);

  const run = useCallback(async () => {
    if (running.current) {
      queued.current = true;
      return;
    }
    running.current = true;
    setState((previous) => ({ status: "running", last: reportOf(previous) }));
    try {
      const report = await runChecksInSandbox(
        filesRef.current.map((file) => file.path),
      );
      setState({ status: "done", report });
    } catch (error) {
      setState({
        status: "error",
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      running.current = false;
    }
    if (queued.current) {
      queued.current = false;
      void run();
    }
  }, []);

  useEffect(
    () =>
      subscribeExecution((outcome) => {
        built.current = !("error" in outcome);
        if (built.current) void run();
      }),
    [run],
  );

  const key = JSON.stringify(files);
  useEffect(() => {
    if (built.current) void run();
  }, [key, run]);

  return { state, run };
}

/** The last finished run's report, kept on screen while the next one runs. */
function reportOf(state: ChecksState): TestReport | undefined {
  if (state.status === "done") return state.report;
  if (state.status === "running") return state.last;
  return undefined;
}

/** How many checks failed in the last finished run. */
export function failureCount(state: ChecksState): number {
  return reportOf(state)?.failed ?? 0;
}

function CheckRow({ result }: { result: TestResult }) {
  const failed = result.status === "failed";
  return (
    <ChangeRow
      icon={failed ? CircleX : CircleCheck}
      title={result.name}
      description={
        failed ? (
          <span className="text-destructive">Failed</span>
        ) : result.status === "passed" ? (
          "Passed"
        ) : (
          "Skipped"
        )
      }
      badge={<Badge variant="outline">{LAYERS[layerOf(result)]}</Badge>}
    >
      {result.requirement && (
        <p className="text-sm text-muted-foreground">{result.requirement}</p>
      )}
      {result.message && (
        <p className="mt-2 font-mono text-xs break-words whitespace-pre-wrap text-destructive">
          {result.message}
        </p>
      )}
    </ChangeRow>
  );
}

function Summary({ report }: { report: TestReport }) {
  return (
    <p className="text-xs text-muted-foreground">
      {report.failed > 0
        ? `${report.failed} failing, ${report.passed} passing`
        : `All ${report.passed} passing`}
    </p>
  );
}

export function TaskChecks({
  state,
  run,
}: {
  state: ChecksState;
  run: () => void;
}) {
  const report = reportOf(state);
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center gap-3 px-4 pt-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-semibold">Checks</h2>
          {report && <Summary report={report} />}
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={state.status === "running"}
          onClick={run}
        >
          {state.status === "running" ? (
            <>
              <Loader2 className="size-3.5 animate-spin" /> Running…
            </>
          ) : (
            <>
              <Play className="size-3.5" /> Run checks
            </>
          )}
        </Button>
      </div>
      {state.status === "error" && (
        <p role="alert" className="px-4 text-sm text-destructive">
          {state.error}
        </p>
      )}
      {report ? (
        <ItemGroup
          className={
            state.status === "running" ? "divide-y opacity-60" : "divide-y"
          }
        >
          {sortChecks(report.results).map((result, index) => (
            <CheckRow key={`${index}:${result.name}`} result={result} />
          ))}
        </ItemGroup>
      ) : state.status === "waiting" ? (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Waiting for the model</EmptyTitle>
            <EmptyDescription>
              Checks run as soon as the model builds.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : null}
    </div>
  );
}
